import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../../shared/components/Layout';
import Modal from '../../shared/components/Modal';
import MultiSelect from '../../shared/components/MultiSelect';
import Pagination from '../../shared/components/Pagination';
import { FIAT_CURRENCIES } from '../../shared/constants/markets';
import {
  listPaymentMethods,
  createPaymentMethod,
  updatePaymentMethod,
  deletePaymentMethod,
  PaymentMethodInput,
} from './paymentMethodsApi';
import { P2pPaymentMethod, PaginatedResponse } from '../../shared/types';
import { useAuth } from '../auth/AuthContext';

const emptyForm: PaymentMethodInput = {
  code: '',
  name: '',
  fiat_currencies: [],
  sort_order: 0,
  is_active: true,
};

const PaymentMethods: React.FC = () => {
  const { admin } = useAuth();
  const [methods, setMethods] = useState<PaginatedResponse<P2pPaymentMethod> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<P2pPaymentMethod | null>(null);
  const [formData, setFormData] = useState<PaymentMethodInput>(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionError, setActionError] = useState('');

  const canManage = admin?.backoffice_role === 'administrator';

  const load = useCallback(async (page: number) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await listPaymentMethods({ page, per_page: 20, search: search || undefined });
      setMethods(data);
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { error?: string } } }).response?.data?.error ?? 'Failed to load payment methods'
        : 'Failed to load payment methods';
      setError(msg);
      setMethods(null);
    } finally {
      setIsLoading(false);
    }
  }, [search]);

  useEffect(() => {
    load(currentPage);
  }, [currentPage, load]);

  const applySearch = () => {
    setSearch(searchInput.trim());
    setCurrentPage(1);
  };

  const openCreate = () => {
    setEditing(null);
    setFormData(emptyForm);
    setActionError('');
    setModalOpen(true);
  };

  const openEdit = (m: P2pPaymentMethod) => {
    setEditing(m);
    setFormData({
      code: m.code,
      name: m.name,
      fiat_currencies: m.fiat_currencies ?? [],
      sort_order: m.sort_order,
      is_active: m.is_active,
    });
    setActionError('');
    setModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setActionError('');
    setIsSubmitting(true);
    try {
      const payload: PaymentMethodInput = {
        name: formData.name.trim(),
        fiat_currencies: formData.fiat_currencies ?? [],
        sort_order: Number(formData.sort_order ?? 0),
        is_active: formData.is_active ?? true,
      };
      if (formData.code && formData.code.trim() !== '') {
        payload.code = formData.code.trim();
      }
      if (editing) {
        await updatePaymentMethod(editing.id, payload);
      } else {
        await createPaymentMethod(payload);
      }
      setModalOpen(false);
      setEditing(null);
      load(currentPage);
    } catch (err: unknown) {
      const resp = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { error?: string; message?: string; errors?: Record<string, string[]> } } }).response
        : undefined;
      const errors = resp?.data?.errors;
      if (errors) {
        const first = Object.values(errors)[0];
        setActionError(Array.isArray(first) ? first[0] : 'Validation failed');
      } else {
        setActionError(resp?.data?.error || resp?.data?.message || 'Failed to save');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (m: P2pPaymentMethod) => {
    if (!window.confirm(`Delete payment method "${m.name}"?`)) return;
    try {
      await deletePaymentMethod(m.id);
      load(currentPage);
    } catch {
      window.alert('Failed to delete');
    }
  };

  const toggleActive = async (m: P2pPaymentMethod) => {
    try {
      await updatePaymentMethod(m.id, { is_active: !m.is_active });
      load(currentPage);
    } catch {
      window.alert('Failed to update status');
    }
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">P2P Payment Methods</h1>
            <p className="text-slate-400 mt-1">Reference list of payment methods used in P2P offers and user profiles</p>
          </div>
          {canManage && (
            <button onClick={openCreate} className="btn btn-primary">
              + Add Method
            </button>
          )}
        </div>

        <div className="card p-4 flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Search</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), applySearch())}
                placeholder="Name or code"
                className="input py-2 w-64"
              />
              <button type="button" onClick={applySearch} className="btn btn-secondary py-2">
                Apply
              </button>
              {search && (
                <button
                  type="button"
                  onClick={() => { setSearchInput(''); setSearch(''); setCurrentPage(1); }}
                  className="btn btn-secondary py-2"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="card overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-12 h-12 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <p className="text-red-400">{error}</p>
              {error.includes('Insufficient permissions') && (
                <p className="text-slate-500 mt-2 text-sm">Your role may not have access to manage payment methods.</p>
              )}
            </div>
          ) : !methods?.data.length ? (
            <div className="p-12 text-center text-slate-400">
              No payment methods found.
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-800/50">
                    <tr>
                      <th className="table-header">ID</th>
                      <th className="table-header">Name</th>
                      <th className="table-header">Code</th>
                      <th className="table-header">Fiat</th>
                      <th className="table-header">Sort</th>
                      <th className="table-header">Active</th>
                      <th className="table-header">Updated</th>
                      {canManage && <th className="table-header text-right">Actions</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {methods.data.map((m) => (
                      <tr key={m.id} className="hover:bg-slate-800/30 transition-colors">
                        <td className="table-cell font-mono text-indigo-400">#{m.id}</td>
                        <td className="table-cell text-white">{m.name}</td>
                        <td className="table-cell font-mono text-slate-400 text-xs">{m.code}</td>
                        <td className="table-cell text-slate-400 text-xs">
                          {m.fiat_currencies?.length ? m.fiat_currencies.join(', ') : 'All fiat'}
                        </td>
                        <td className="table-cell text-slate-400">{m.sort_order}</td>
                        <td className="table-cell">
                          <span className={`badge ${m.is_active ? 'badge-success' : 'badge-warning'}`}>
                            {m.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="table-cell text-slate-400">{new Date(m.updated_at).toLocaleString()}</td>
                        {canManage && (
                          <td className="table-cell text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => toggleActive(m)}
                                className="px-3 py-1.5 text-xs font-medium text-slate-300 hover:text-white bg-slate-700/40 hover:bg-slate-700 rounded-lg transition-all"
                              >
                                {m.is_active ? 'Deactivate' : 'Activate'}
                              </button>
                              <button
                                onClick={() => openEdit(m)}
                                className="px-3 py-1.5 text-xs font-medium text-indigo-400 hover:text-indigo-300 bg-indigo-500/10 hover:bg-indigo-500/20 rounded-lg transition-all"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDelete(m)}
                                className="px-3 py-1.5 text-xs font-medium text-red-400 hover:text-red-300 bg-red-500/10 hover:bg-red-500/20 rounded-lg transition-all"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {methods.last_page > 1 && (
                <div className="px-6 py-4 border-t border-slate-800">
                  <Pagination
                    currentPage={currentPage}
                    lastPage={methods.last_page}
                    onPageChange={setCurrentPage}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Modal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setEditing(null); }}
        title={editing ? 'Edit payment method' : 'Add payment method'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {actionError && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {actionError}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
              className="input"
              placeholder="e.g. Bank Transfer"
              required
              maxLength={128}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Code (optional)</label>
            <input
              type="text"
              value={formData.code ?? ''}
              onChange={(e) => setFormData((p) => ({ ...p, code: e.target.value }))}
              className="input font-mono text-sm"
              placeholder="auto-generated from name"
              maxLength={64}
              pattern="[A-Za-z0-9_\-]+"
            />
            <p className="text-xs text-slate-500 mt-1">
              Letters, digits, underscore, dash. Leave empty to auto-generate.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Fiat currencies</label>
            <MultiSelect
              options={FIAT_CURRENCIES}
              value={formData.fiat_currencies ?? []}
              onChange={(vals) => setFormData((p) => ({ ...p, fiat_currencies: vals }))}
              placeholder="All fiat"
            />
            <p className="text-xs text-slate-500 mt-1">
              Leave empty to make the method available for all fiat currencies.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Sort order</label>
              <input
                type="number"
                value={formData.sort_order ?? 0}
                onChange={(e) => setFormData((p) => ({ ...p, sort_order: Number(e.target.value) }))}
                className="input"
                min={0}
                max={100000}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Status</label>
              <select
                value={formData.is_active ? '1' : '0'}
                onChange={(e) => setFormData((p) => ({ ...p, is_active: e.target.value === '1' }))}
                className="input"
              >
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
            </div>
          </div>
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={() => { setModalOpen(false); setEditing(null); }}
              className="flex-1 btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !formData.name.trim()}
              className="flex-1 btn btn-primary disabled:opacity-50"
            >
              {isSubmitting ? 'Saving...' : editing ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>
    </Layout>
  );
};

export default PaymentMethods;
